import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 10 — "My round" (D-206-010 row 10, kept as value + link).
 *
 * **Who does what:** the person declares what he is raising, how much and on what terms; Raisy then
 * finds and qualifies the investors who fit that round instead of everyone who exists.
 * **What he sees:** the round Raisy is working from — the stage, the amount, the ticket and the date
 * it was first declared — and the way to change it.
 * **What he feels:** *"stop wasting weeks on non-fit investors"* — Maya's W1 want, the whole reason
 * the investor journey exists.
 *
 * WHY IT LINKS RATHER THAN EDITING: the round is declared inside the outreach journey, where the
 * sector and geography facets that shape the investor pool are chosen right next to it. Editing it
 * here would put the round and the pool it produces on two different screens — and the round's own
 * values are the input to that pool, so the two must be seen together.
 *
 * ⚠️ AN EMPTY ROUND SAYS WHAT TO DO, NOT THAT SOMETHING IS MISSING: with nothing declared, the value
 * is *"no round declared yet"* plus the one action that changes it. The date is shown when it exists
 * because a declared round has a start, and *"when did I start this?"* is a question this product
 * should answer rather than make him remember.
 */
export const RoundSection = ({ settings }: { settings: UserSettings }) => {
  const { round } = settings;

  const value =
    round.state === "loading" ? (
      <span className="text-muted-foreground">Reading your round…</span>
    ) : round.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {round.reason}.
      </span>
    ) : (
      (() => {
        const { raising_stage, raising_amount, check_size, sector, declared_at } = round.value;
        const declared = Boolean(raising_stage || raising_amount || declared_at || check_size);
        if (!declared) {
          return (
            <span className="flex flex-col gap-1">
              <span>
                No round declared yet
                <span className="ml-2 font-normal text-muted-foreground">
                  Raisy searches for investors once it knows what you are raising
                </span>
              </span>
            </span>
          );
        }
        const figures = [raising_amount, check_size ? `tickets ${check_size}` : null]
          .filter(Boolean)
          .join(" · ");
        return (
          <span className="flex flex-col gap-1">
            <span>
              {raising_stage || "round declared"}
              {figures && <span className="ml-2 font-normal text-muted-foreground">{figures}</span>}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {[
                sector ? `${sector}` : null,
                declared_at ? `declared ${new Date(declared_at).toLocaleDateString()}` : null,
              ]
                .filter(Boolean)
                .join(" · ") || "no sector or date recorded with it"}
            </span>
          </span>
        );
      })()
    );

  return (
    <SettingsSection
      title="My round"
      hint="What you are raising, so Raisy looks for the investors who fit it."
      value={value}
      link={{ to: "/outreach", label: "Open your round" }}
    />
  );
};
